// ============================================
// FILE: /frontend/src/components/listings/QuickFiltersBar.jsx
// PURPOSE: Quick filter pills component - ALIGNED WITH PRISMA SCHEMA
// ============================================

import { useState } from 'react';
import { Filter, X, ChevronDown } from 'lucide-react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';

// ============================================
// FILTER DEFINITIONS - Based on actual schema fields
// ============================================

const VEHICLE_FILTERS = [
  { 
    id: 'under_1m', 
    label: 'Under 1M', 
    icon: '💰',
    filter: { price: { lte: 1000000 } }
  },
  { 
    id: 'under_3m', 
    label: 'Under 3M', 
    icon: '💵',
    filter: { price: { lte: 3000000 } }
  },
  //{ 
    //id: 'verified_dealers', 
    //label: 'Verified Dealers', 
    //icon: '✓',
    //filter: { user: { role: 'DEALER' } }
  //},
  { 
    id: 'premium_sellers', 
    label: 'Premium Sellers', 
    icon: '⭐',
    filter: { user: { subscriptionTier: { in: ['GOLD', 'PLATINUM'] } } }
  },
  { 
    id: 'this_week', 
    label: 'This Week', 
    icon: '🆕',
    filter: { createdAt: { gte: 'THIS_WEEK' } } // Handled specially in applyFilter
  },
  { 
    id: 'featured', 
    label: 'Featured', 
    icon: '🌟',
    filter: { isFeatured: true }
  },
  { 
    id: 'most_viewed', 
    label: 'Most Viewed', 
    icon: '👁️',
    filter: { orderBy: { viewsCount: 'desc' } }
  },
];

const PROPERTY_FILTERS = [
  { 
    id: 'under_5m', 
    label: 'Under 5M', 
    icon: '💰',
    filter: { price: { lte: 5000000 } }
  },
  { 
    id: 'under_10m', 
    label: 'Under 10M', 
    icon: '💵',
    filter: { price: { lte: 10000000 } }
  },
  { 
    id: 'under_20m', 
    label: 'Under 20M', 
    icon: '💎',
    filter: { price: { lte: 20000000 } }
  },
  //{ 
    //id: 'verified_agents', 
    //label: 'Verified Agents', 
    //icon: '✓',
    //filter: { user: { role: 'REAL_ESTATE_AGENT' } }
  //},
  { 
    id: 'premium_sellers', 
    label: 'Premium Sellers', 
    icon: '⭐',
    filter: { user: { subscriptionTier: { in: ['GOLD', 'PLATINUM'] } } }
  },
  { 
    id: 'group_buy', 
    label: 'Chama/Group Buy', 
    icon: '👥',
    filter: { allowGroupBuy: true }
  },
  { 
    id: 'this_week', 
    label: 'This Week', 
    icon: '🆕',
    filter: { createdAt: { gte: 'THIS_WEEK' } }
  },
  { 
    id: 'featured', 
    label: 'Featured', 
    icon: '🌟',
    filter: { isFeatured: true }
  },
  { 
    id: 'most_inquired', 
    label: 'Most Inquired', 
    icon: '📩',
    filter: { orderBy: { inquiriesCount: 'desc' } }
  },
];

// Helper to get date for "this week" filter
const getThisWeekDate = () => {
  const now = new Date();
  const dayOfWeek = now.getDay();
  const startOfWeek = new Date(now);
  startOfWeek.setDate(now.getDate() - dayOfWeek);
  startOfWeek.setHours(0, 0, 0, 0);
  return startOfWeek.toISOString();
};

// ============================================
// MAIN COMPONENT
// ============================================

export default function QuickFiltersBar({ 
  category = 'VEHICLE', 
  onFilterApply,
  className = '' 
}) {
  const [activeFilters, setActiveFilters] = useState([]);
  const [showAll, setShowAll] = useState(false);

  // Select filters based on category
  const filters = category === 'VEHICLE' ? VEHICLE_FILTERS : PROPERTY_FILTERS;
  
  // Show limited filters on mobile, all on desktop or when expanded
  const visibleFilters = showAll ? filters : filters.slice(0, 5);

  // Process filter to handle special cases like THIS_WEEK
  const processFilter = (filterConfig) => {
    const processed = { ...filterConfig.filter };
    
    // Handle THIS_WEEK special value
    if (processed.createdAt?.gte === 'THIS_WEEK') {
      processed.createdAt = { gte: getThisWeekDate() };
    }
    
    return processed;
  };

  // Handle filter toggle
  const handleFilterClick = (filterId) => {
    let newActiveFilters;
    
    if (activeFilters.includes(filterId)) {
      // Remove filter
      newActiveFilters = activeFilters.filter(id => id !== filterId);
    } else {
      // Check for mutually exclusive filters (price ranges)
      const priceFilters = ['under_1m', 'under_3m', 'under_5m', 'under_10m', 'under_20m'];
      const orderFilters = ['most_viewed', 'most_inquired'];
      
      if (priceFilters.includes(filterId)) {
        // Remove other price filters
        newActiveFilters = activeFilters.filter(id => !priceFilters.includes(id));
      } else if (orderFilters.includes(filterId)) {
        // Remove other order filters
        newActiveFilters = activeFilters.filter(id => !orderFilters.includes(id));
      } else {
        newActiveFilters = [...activeFilters];
      }
      
      newActiveFilters.push(filterId);
    }
    
    setActiveFilters(newActiveFilters);
    
    // Build combined filter object
    if (newActiveFilters.length === 0) {
      onFilterApply(null);
    } else {
      const combinedFilter = {
        where: {},
        orderBy: undefined
      };
      
      newActiveFilters.forEach(id => {
        const filterConfig = filters.find(f => f.id === id);
        if (filterConfig) {
          const processed = processFilter(filterConfig);
          
          // Handle orderBy separately
          if (processed.orderBy) {
            combinedFilter.orderBy = processed.orderBy;
          } else {
            // Merge where conditions
            Object.assign(combinedFilter.where, processed);
          }
        }
      });
      
      onFilterApply(combinedFilter);
    }
  };

  // Clear all filters
  const clearAllFilters = () => {
    setActiveFilters([]);
    onFilterApply(null);
  };

  return (
    <div className={`bg-white border rounded-lg p-2 sm:p-3 shadow-sm ${className}`}>
      {/* Header */}
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-1.5 sm:gap-2">
          <Filter className="w-3 h-3 sm:w-4 sm:h-4 text-muted-foreground" />
          <span className="text-[10px] sm:text-xs md:text-sm font-medium text-muted-foreground">
            Quick Filters:
          </span>
          {activeFilters.length > 0 && (
            <Badge variant="secondary" className="text-[10px] sm:text-xs">
              {activeFilters.length} active
            </Badge>
          )}
        </div>
        
        {activeFilters.length > 0 && (
          <Button
            variant="ghost"
            size="sm"
            onClick={clearAllFilters}
            className="h-6 px-2 text-[10px] sm:text-xs text-muted-foreground hover:text-destructive"
          >
            <X className="w-3 h-3 mr-1" />
            Clear all
          </Button>
        )}
      </div>

      {/* Filter Pills */}
      <div className="flex flex-wrap gap-1.5 sm:gap-2">
        {visibleFilters.map((filter) => {
          const isActive = activeFilters.includes(filter.id);
          
          return (
            <Button
              key={filter.id}
              variant={isActive ? "default" : "outline"}
              size="sm"
              onClick={() => handleFilterClick(filter.id)}
              className={`
                h-7 sm:h-8 px-2 sm:px-3 text-[10px] sm:text-xs
                transition-all duration-200
                ${isActive 
                  ? 'bg-primary text-primary-foreground shadow-sm' 
                  : 'hover:bg-accent hover:border-primary/50'
                }
              `}
            >
              <span className="mr-1">{filter.icon}</span>
              {filter.label}
              {isActive && (
                <X className="w-3 h-3 ml-1 opacity-70" />
              )}
            </Button>
          );
        })}
        
        {/* Show More/Less Button */}
        {filters.length > 5 && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setShowAll(!showAll)}
            className="h-7 sm:h-8 px-2 sm:px-3 text-[10px] sm:text-xs text-muted-foreground"
          >
            {showAll ? (
              <>Show Less</>
            ) : (
              <>
                +{filters.length - 5} More
                <ChevronDown className="w-3 h-3 ml-1" />
              </>
            )}
          </Button>
        )}
      </div>

      {/* Active Filters Summary (optional - shows what's being filtered) */}
      {activeFilters.length > 0 && (
        <div className="mt-2 pt-2 border-t">
          <p className="text-[10px] sm:text-xs text-muted-foreground">
            Showing: {' '}
            {activeFilters.map((id, index) => {
              const filter = filters.find(f => f.id === id);
              return (
                <span key={id}>
                  {filter?.label}
                  {index < activeFilters.length - 1 ? ', ' : ''}
                </span>
              );
            })}
          </p>
        </div>
      )}
    </div>
  );
}